import React, { useState, useEffect } from "react";
import CompanyLayout from "../../components/layouts/CompanyLayout";
import { auth, firestore, database } from "../../firebaseConfig";
import { doc, getDoc, setDoc, collection, getDocs, query, where } from "firebase/firestore";
import { ref, onValue, remove } from "firebase/database";
import toast from "react-hot-toast";
import { UserCheck, Clock, CheckCircle, XCircle, Shield, Users as UsersIcon } from "lucide-react";

const Employees = () => {
  const [activeTab, setActiveTab] = useState("requests");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [allowEmployeeRequests, setAllowEmployeeRequests] = useState(true);
  const [updatingRequestsSetting, setUpdatingRequestsSetting] = useState(false);

  const tabs = [
    { id: "requests", label: "Pending Requests", icon: "📩" },
    { id: "employees", label: "Manage Employees", icon: "👥" },
  ];

  const roles = [
    { value: "Manager", label: "Manager", description: "Second in command, can approve most operations" },
    { value: "Web Operator", label: "Web Operator", description: "Casual worker, needs approval for edits/deletes" },
    { value: "Auditor", label: "Auditor", description: "View and audit records, some operations need approval" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "suspended", label: "Suspended" },
    { value: "removed", label: "Removed" },
  ];

  // Normalize UI status selection to the persisted employeeStatus value
  const mapStatusToEmployeeStatus = (status) => (status === "active" ? "approved" : status);
  const REMOVED_EMPLOYEE_FIELDS_TO_CLEAR = {
    companyName: null,
    employeeRole: null,
    requestedCompany: null,
  };

  const refreshEmployees = async () => {
    if (!companyName) return;
    const employeesRef = collection(firestore, "companies", companyName, "employees");
    const employeesSnapshot = await getDocs(employeesRef);
    const employeesList = [];
    employeesSnapshot.forEach((doc) => {
      employeesList.push({ id: doc.id, ...doc.data() });
    });
    setEmployees(employeesList);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          window.location.href = "/login";
          return;
        }

        // Get user data and check if admin
        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCompanyName(userData.companyName);

          // Check if user is company admin
          const companyKey = userData.companyName;
          if (companyKey) {
            const companyDoc = await getDoc(doc(firestore, "companies", companyKey));
            if (companyDoc.exists()) {
              const companyData = companyDoc.data();
              setIsAdmin(companyData.adminUid === user.uid);
              setAllowEmployeeRequests(companyData.allowEmployeeRequests !== false);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Listen to pending employee requests
  useEffect(() => {
    if (!companyName) return;

    const requestsRef = ref(database, `Companies/${companyName}/EmployeeRequests`);
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const requestsList = Object.entries(data).map(([uid, request]) => ({
          uid,
          ...request,
        }));
        setPendingRequests(requestsList);
      } else {
        setPendingRequests([]);
      }
    });

    return () => unsubscribe();
  }, [companyName]);

  // Fetch employees from company subcollection
  useEffect(() => {
    if (!companyName) return;

    const fetchEmployees = async () => {
      try {
        const employeesRef = collection(firestore, "companies", companyName, "employees");
        const employeesSnapshot = await getDocs(employeesRef);
        const employeesList = [];
        employeesSnapshot.forEach((doc) => {
          employeesList.push({ id: doc.id, ...doc.data() });
        });
        setEmployees(employeesList);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    fetchEmployees();
  }, [companyName]);

  const handleApproveRequest = async (request, selectedRole) => {
    if (!selectedRole) {
      toast.error("Please select a role for the employee");
      return;
    }

    try {
      // Define permissions based on role
      let permissions = {};
      switch (selectedRole) {
        case "Manager":
          permissions = {
            canAddPayments: true,
            canEditRecords: true,
            canDeleteRecords: true,
            canApprovePayments: true,
            canViewReports: true,
            canManageSettings: true,
          };
          break;
        case "Web Operator":
          permissions = {
            canAddPayments: false,
            canEditRecords: false, // needs approval
            canDeleteRecords: false, // needs approval
            canApprovePayments: false,
            canViewReports: true,
            canManageSettings: false,
          };
          break;
        case "Auditor":
          permissions = {
            canAddPayments: false,
            canEditRecords: false, // needs approval
            canDeleteRecords: false,
            canApprovePayments: false, // needs approval
            canViewReports: true,
            canManageSettings: false,
          };
          break;
        default:
          permissions = {
            canAddPayments: false,
            canEditRecords: false,
            canDeleteRecords: false,
            canApprovePayments: false,
            canViewReports: true,
            canManageSettings: false,
          };
      }

      // Create employee record in company subcollection
      await setDoc(
        doc(firestore, "companies", companyName, "employees", request.uid),
        {
          uid: request.uid,
          fullName: request.fullName,
          email: request.email,
          role: selectedRole,
          permissions: permissions,
          approvedAt: Date.now(),
          status: "active",
        }
      );

      // Update user document
      await setDoc(
        doc(firestore, "users", request.uid),
        {
          companyName: companyName,
          employeeRole: selectedRole,
          employeeStatus: "approved",
          approvedAt: Date.now(),
        },
        { merge: true }
      );

      // Remove request from RTDB
      await remove(ref(database, `Companies/${companyName}/EmployeeRequests/${request.uid}`));

      toast.success(`${request.fullName} approved as ${selectedRole}`);

      // Refresh employees list
      await refreshEmployees();
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("Failed to approve request");
    }
  };

  const handleDeclineRequest = async (request) => {
    try {
      // Update user document
      await setDoc(
        doc(firestore, "users", request.uid),
        {
          employeeStatus: "declined",
          declinedAt: Date.now(),
          requestedCompany: null,
        },
        { merge: true }
      );

      // Remove request from RTDB
      await remove(ref(database, `Companies/${companyName}/EmployeeRequests/${request.uid}`));

      toast.success("Request declined");
    } catch (error) {
      console.error("Error declining request:", error);
      toast.error("Failed to decline request");
    }
  };

  const handleUpdateEmployeeRole = async (employeeId, newRole) => {
    const target = employees.find((emp) => emp.id === employeeId);
    if (target?.role === "CompanyAdmin") {
      toast.error("Company Admin role cannot be changed");
      return;
    }
    try {
      let permissions = {};
      switch (newRole) {
        case "Manager":
          permissions = {
            canAddPayments: true,
            canEditRecords: true,
            canDeleteRecords: true,
            canApprovePayments: true,
            canViewReports: true,
            canManageSettings: true,
          };
          break;
        case "Web Operator":
          permissions = {
            canAddPayments: false,
            canEditRecords: false,
            canDeleteRecords: false,
            canApprovePayments: false,
            canViewReports: true,
            canManageSettings: false,
          };
          break;
        case "Auditor":
          permissions = {
            canAddPayments: false,
            canEditRecords: false,
            canDeleteRecords: false,
            canApprovePayments: false,
            canViewReports: true,
            canManageSettings: false,
          };
          break;
        default:
          permissions = {};
      }

      // Update employee in company subcollection
      await setDoc(
        doc(firestore, "companies", companyName, "employees", employeeId),
        {
          role: newRole,
          permissions: permissions,
          updatedAt: Date.now(),
        },
        { merge: true }
      );

      // Update user document
      await setDoc(
        doc(firestore, "users", employeeId),
        {
          employeeRole: newRole,
          updatedAt: Date.now(),
        },
        { merge: true }
      );

      toast.success("Employee role updated");

      // Refresh employees list
      await refreshEmployees();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    }
  };

  const handleUpdateEmployeeStatus = async (employeeId, newStatus) => {
    const target = employees.find((emp) => emp.id === employeeId);
    if (target?.role === "CompanyAdmin") {
      toast.error("Company Admin status cannot be changed");
      return;
    }

    try {
      const userStatus = mapStatusToEmployeeStatus(newStatus);
      const userUpdate = {
        employeeStatus: userStatus,
        updatedAt: Date.now(),
      };

      if (newStatus === "removed") {
        Object.assign(userUpdate, REMOVED_EMPLOYEE_FIELDS_TO_CLEAR);
      }

      await setDoc(
        doc(firestore, "companies", companyName, "employees", employeeId),
        {
          status: newStatus,
          updatedAt: Date.now(),
        },
        { merge: true }
      );

      await setDoc(
        doc(firestore, "users", employeeId),
        userUpdate,
        { merge: true }
      );

      toast.success("Employee status updated");

      await refreshEmployees();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
      throw error;
    }
  };

  const handleToggleRequests = async (nextValue) => {
    if (!companyName) return;
    setUpdatingRequestsSetting(true);
    try {
      await setDoc(
        doc(firestore, "companies", companyName),
        { allowEmployeeRequests: nextValue },
        { merge: true }
      );
      setAllowEmployeeRequests(nextValue);
      toast.success(
        nextValue ? "Now accepting employee requests" : "Employee requests are blocked"
      );
    } catch (error) {
      console.error("Error updating request setting:", error);
      toast.error("Could not update request preference");
    } finally {
      setUpdatingRequestsSetting(false);
    }
  };

  if (loading) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Loading...</p>
        </div>
      </CompanyLayout>
    );
  }

  if (!isAdmin) {
    return (
      <CompanyLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <Shield className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only company admins can access this page.</p>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="p-6 text-gray-800 transition-all duration-300">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-cyan-700 flex items-center gap-2">
            <UserCheck size={32} />
            Employee Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage employee requests and roles for your company
          </p>
        </div>

        <div className="mb-6 bg-white border border-gray-100 rounded-xl shadow-sm p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold">Employee Requests</p>
            <p className="text-gray-700">
              Control whether your company is currently accepting new employee requests.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Default is to allow requests. Toggle off to pause incoming employee join requests.
            </p>
          </div>
          <button
            onClick={() => handleToggleRequests(!allowEmployeeRequests)}
            disabled={updatingRequestsSetting}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
              allowEmployeeRequests
                ? "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                : "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
            } ${updatingRequestsSetting ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                allowEmployeeRequests ? "bg-green-500" : "bg-red-500"
              }`}
            />
            {allowEmployeeRequests ? "Accepting Requests" : "Not Accepting Requests"}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-gray-200 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-2 px-4 font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === tab.id
                  ? "text-cyan-700 border-b-2 border-cyan-700"
                  : "text-gray-500 hover:text-cyan-600"
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "requests" && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">
              Pending Employee Requests
            </h2>

            {pendingRequests.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No pending requests</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {pendingRequests.map((request) => (
                  <EmployeeRequestCard
                    key={request.uid}
                    request={request}
                    roles={roles}
                    onApprove={handleApproveRequest}
                    onDecline={handleDeclineRequest}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "employees" && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">
              Current Employees
            </h2>

            {employees.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No employees yet</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employees.map((employee) => (
                      <EmployeeRow
                        key={employee.id}
                        employee={employee}
                        roles={roles}
                        statusOptions={statusOptions}
                        onUpdateRole={handleUpdateEmployeeRole}
                        onUpdateStatus={handleUpdateEmployeeStatus}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </CompanyLayout>
  );
};

// Component for displaying employee request card
const EmployeeRequestCard = ({ request, roles, onApprove, onDecline }) => {
  const [selectedRole, setSelectedRole] = useState("");
  const [showRoleInfo, setShowRoleInfo] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{request.fullName}</h3>
          <p className="text-gray-600 text-sm">{request.email}</p>
          <p className="text-gray-500 text-xs mt-1">
            Requested: {new Date(request.requestedAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onDecline(request)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2"
          >
            <XCircle size={16} />
            Decline
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Assign Role
          </label>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          >
            <option value="">Select a role...</option>
            {roles.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </div>

        {selectedRole && (
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>{selectedRole}:</strong>{" "}
              {roles.find((r) => r.value === selectedRole)?.description}
            </p>
          </div>
        )}

        <button
          onClick={() => onApprove(request, selectedRole)}
          disabled={!selectedRole}
          className={`w-full py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
            selectedRole
              ? "bg-green-600 text-white hover:bg-green-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          <CheckCircle size={16} />
          Approve with {selectedRole || "Selected Role"}
        </button>
      </div>
    </div>
  );
};

// Component for displaying employee row in table
const EmployeeRow = ({ employee, roles, statusOptions, onUpdateRole, onUpdateStatus }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newRole, setNewRole] = useState(employee.role);
  const [currentStatus, setCurrentStatus] = useState(employee.status || "active");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const isCompanyAdmin = employee.role === "CompanyAdmin";

  useEffect(() => {
    setNewRole(employee.role);
    setCurrentStatus(employee.status || "active");
  }, [employee.role, employee.status]);

  const handleSave = () => {
    if (newRole !== employee.role) {
      onUpdateRole(employee.id, newRole);
    }
    setIsEditing(false);
  };

  const handleStatusChange = async (value) => {
    const previous = currentStatus;
    setStatusUpdating(true);
    try {
      await onUpdateStatus(employee.id, value);
      setCurrentStatus(value);
    } catch (error) {
      setCurrentStatus(previous);
    } finally {
      setStatusUpdating(false);
    }
  };

  const statusBadgeClass =
    currentStatus === "suspended"
      ? "bg-amber-100 text-amber-800"
      : currentStatus === "removed"
      ? "bg-red-100 text-red-800"
      : "bg-green-100 text-green-800";

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">{employee.fullName}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-600">{employee.email}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {isEditing ? (
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500"
          >
            {roles.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        ) : (
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
            employee.role === "Manager"
              ? "bg-purple-100 text-purple-800"
              : employee.role === "Auditor"
              ? "bg-blue-100 text-blue-800"
              : "bg-gray-100 text-gray-800"
          }`}>
            {employee.role}
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col gap-2">
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadgeClass}`}>
            {currentStatus}
          </span>
          <select
            value={currentStatus}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={statusUpdating || isCompanyAdmin}
            className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100"
          >
            {statusOptions.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          {isCompanyAdmin && (
            <span className="text-xs text-gray-400">Admin status locked</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        {isEditing ? (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="text-green-600 hover:text-green-900"
            >
              Save
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setNewRole(employee.role);
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
          </div>
        ) : isCompanyAdmin ? (
          <span className="text-gray-400 cursor-not-allowed">Edit Role</span>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="text-cyan-600 hover:text-cyan-900"
          >
            Edit Role
          </button>
        )}
      </td>
    </tr>
  );
};

export default Employees;
